import { Checkbox, ChevronDownIcon, HStack, Popover, Pressable, VStack } from 'native-base';
import { useTranslation } from 'react-i18next';

import { Text } from '@pimm/base';

const GROUP_KEY: Record<string, string> = {
  Gateways: 'store_equipment:gateways',
  'Walk-ins': 'store_equipment:walk_ins',
  Coolers: 'store_equipment:coolers_group',
  Freezers: 'store_equipment:freezers',
  Other: 'store_equipment:other',
};

type GroupFilterProps = {
  groups: string[];
  value: string[];
  onChange: (groups: string[]) => void;
};

export const GroupFilter = ({ groups, value, onChange }: GroupFilterProps) => {
  const { t } = useTranslation();
  const isAllSelected = value.length === 0;
  const hasFilter = value.length > 0;

  const toggle = (group: string) => {
    if (value.includes(group)) {
      onChange(value.filter(g => g !== group));
    } else {
      onChange([...value, group]);
    }
  };

  const handleToggle = (group: string) => () => toggle(group);
  const handleClearAll = () => onChange([]);

  return (
    <Popover
      placement="bottom right"
      trigger={triggerProps => (
        <Pressable
          {...triggerProps}
          rounded="lg"
          px={3}
          h={9}
          shadow={1}
          justifyContent="center"
          borderWidth={1}
          borderColor={hasFilter ? 'primary.600' : 'gray.300'}
          bg="white"
          _pressed={{ opacity: 0.8 }}
        >
          <HStack space={1} alignItems="center">
            <Text size="sm" fontWeight={500} color={hasFilter ? 'primary.600' : 'gray.700'}>
              {t('store_equipment:all_groups')}
            </Text>
            <ChevronDownIcon size={3} color={hasFilter ? 'primary.600' : 'gray.500'} />
          </HStack>
        </Pressable>
      )}
    >
      <Popover.Content rounded="lg" borderWidth={0} w={240}>
        <Popover.Body p={2} bg="transparent">
          <VStack bgColor="white" shadow={1} rounded="lg">
            <Pressable onPress={handleClearAll} _pressed={{ bg: 'gray.50' }}>
              <HStack p={3} alignItems="center" space={2}>
                <Checkbox value="all" isChecked={isAllSelected} isReadOnly accessibilityLabel={t('store_equipment:all_groups')} />
                <Text size="md" fontWeight={isAllSelected ? 600 : 400} color="gray.700">
                  {t('store_equipment:all_groups')}
                </Text>
              </HStack>
            </Pressable>

            {groups.map(group => {
              const isChecked = value.includes(group);
              const labelKey = GROUP_KEY[group];
              const label = labelKey ? t(labelKey) : group;
              return (
                <Pressable key={group} onPress={handleToggle(group)} _pressed={{ bg: 'gray.50' }}>
                  <HStack p={3} alignItems="center" space={2}>
                    <Checkbox value={group} isChecked={isChecked} isReadOnly accessibilityLabel={label} />
                    <Text size="md" fontWeight={isChecked ? 600 : 400} color="gray.700">
                      {label}
                    </Text>
                  </HStack>
                </Pressable>
              );
            })}
          </VStack>
        </Popover.Body>
      </Popover.Content>
    </Popover>
  );
};
